/**
 * F18 — NIPE tool: inspectTenant (merged probe).
 *
 * Combines the two original Round-0 probes:
 *   - kind: 'domain'   — resolves tenant info from a domain (no auth).
 *   - kind: 'app-reg'  — checks Repancy app-reg consent state in the
 *     target tenant via app-only Graph token.
 *
 * Round 3 lock: 5 tools (not 6); `probe-tenant-domain` + `query-azure-app-reg`
 * merged into this one tool with two sub-modes.
 */

import type { NipeTool } from './types';

export interface InspectTenantDomainResult {
  kind: 'ok';
  tenantId: string;
  issuer: string;
  region: string | null;
}

export interface InspectTenantAppRegResult {
  kind: 'ok';
  consented: boolean;
  missingScopes: string[];
  tenantDisplayName: string | null;
}

export interface InspectTenantErrorResult {
  kind: 'error';
  reason: string;
  message: string;
}

export interface InspectTenantContext {
  /** Optional. Only present when the SPA wires the tenant-discovery probes. */
  inspectTenant?: (args:
    | { kind: 'domain'; domain: string }
    | { kind: 'app-reg'; tenantId: string },
  ) => Promise<
    InspectTenantDomainResult | InspectTenantAppRegResult | InspectTenantErrorResult
  >;
}

export const inspectTenantTool: NipeTool<InspectTenantContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'inspectTenant',
      description:
        'Inspect a Microsoft tenant. Use kind=domain to resolve a domain to a tenantId via the public OIDC well-known config. Use kind=app-reg with a known tenantId to check whether Repancy has admin consent in that tenant.',
      parameters: {
        type: 'object',
        properties: {
          kind: {
            type: 'string',
            description: 'Either "domain" or "app-reg".',
          },
          domain: {
            type: 'string',
            description: 'When kind=domain: the tenant primary domain (e.g. "morrison.com").',
          },
          tenantId: {
            type: 'string',
            description: 'When kind=app-reg: the tenant GUID returned by an earlier kind=domain probe.',
          },
        },
        required: ['kind'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    if (!ctx.inspectTenant) {
      return { kind: 'error', reason: 'unavailable', message: 'Tenant inspection is not available in this session.' };
    }
    const kind = typeof args.kind === 'string' ? args.kind : '';
    if (kind === 'domain') {
      const domain = typeof args.domain === 'string' ? args.domain : '';
      if (!domain) {
        return { kind: 'error', reason: 'invalid-args', message: 'domain required when kind=domain' };
      }
      return await ctx.inspectTenant({ kind: 'domain', domain });
    }
    if (kind === 'app-reg') {
      const tenantId = typeof args.tenantId === 'string' ? args.tenantId : '';
      if (!tenantId) {
        return { kind: 'error', reason: 'invalid-args', message: 'tenantId required when kind=app-reg' };
      }
      return await ctx.inspectTenant({ kind: 'app-reg', tenantId });
    }
    return { kind: 'error', reason: 'invalid-args', message: 'kind must be "domain" or "app-reg"' };
  },
};
